import { useReactiveVar } from '@apollo/client';

import { useCallback } from 'react';

import { useChooseGrowthTrackMutation, useMyGrowthQuery } from 'core/graphql';

import { authState } from 'core/state';
import { isCareerViewer } from 'core/hooks/CareerCenter/isCareerViewer';
import { IGrowthOverview, transformGrowth } from './transform';

interface IGrowthHook {
  growth: IGrowthOverview | null;
  loading: boolean;
  chooseTrack: (code: string) => Promise<boolean>;
  choosing: boolean;
}

// 🔴 Запрос уходит ТОЛЬКО зрителю карьерного раздела. Без этого условия
// преподаватель, открывший /growth по прямому адресу, получал пачку
// молчаливых PermissionDenied и полупустой экран: GraphQL-ошибки в клиенте
// не редиректят и не показывают тост, поэтому симптом выглядел как «раздел
// сломан», а в консоли лежала причина, которую никто не читает.
/**
 * ТЗ 0039 шаг 4 — экран «Мой рост».
 *
 * `cache-and-network`: очки и медали меняются от действий на ДРУГИХ экранах
 * (заполнил резюме, скачал PDF, прошёл урок), и показывать студенту кэш
 * недельной давности значит показывать ему неправду о его же прогрессе.
 *
 * Выбор трека перечитывает запрос ЭТИМ документом, а не по имени операции:
 * рефетч по имени промахивается молча, если запрос где-то вызван с другими
 * переменными (шрам ТЗ 0033).
 */
export const useGrowth = (): IGrowthHook => {
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const skip = !isCareerViewer(active_role) || Boolean(isDemoUser);

  const { data, loading, refetch } = useMyGrowthQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip,
  });
  const [choose, { loading: choosing }] = useChooseGrowthTrackMutation();

  const chooseTrack = useCallback(
    async (code: string) => {
      const { data: result } = await choose({ variables: { trackCode: code } });
      const ok = Boolean(result?.chooseGrowthTrack?.success);
      if (ok) await refetch();
      return ok;
    },
    [choose, refetch],
  );

  return {
    growth: transformGrowth(data?.myGrowth),
    loading,
    chooseTrack,
    choosing,
  };
};
